import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { adaptEchartsOptions, SliderComponent, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { LineChart } from 'echarts/charts';
import { GridComponent, MarkLineComponent, MarkPointComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import katex from 'katex';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import { buildChartOptions } from './linear-transform-playground.util';

echarts.use([LineChart, SVGRenderer, GridComponent, MarkLineComponent, MarkPointComponent]);

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html' });
}

export interface LinearTransformPlaygroundParams {
  muX: number;
  sigmaX: number;
  initialA: number;
  initialB: number;
  chartHeight?: number;
}

const DEFAULT_PARAMS: LinearTransformPlaygroundParams = {
  muX: 5,
  sigmaX: 1.5,
  initialA: 1,
  initialB: 0,
  chartHeight: 320,
};

const ORANGE = '\\color{#f97316}';
const INDIGO = '\\color{#6366f1}';

@Component({
  selector: 'dojo-linear-transform-playground',
  standalone: true,
  imports: [NgxEchartsDirective, SliderComponent],
  templateUrl: './linear-transform-playground.component.html',
  styleUrl: './linear-transform-playground.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [provideEchartsCore({ echarts })],
  host: { class: 'block' },
})
export class LinearTransformPlaygroundComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });

    effect(
      () => {
        const p = this.params();
        this.a.set(p.initialA);
        this.b.set(p.initialB);
      },
      { allowSignalWrites: true },
    );
  }

  private readonly isDark;
  private readonly sanitizer = inject(DomSanitizer);

  readonly initOpts = { renderer: 'svg' } as const;
  readonly params = input<LinearTransformPlaygroundParams>(DEFAULT_PARAMS);

  readonly a = signal(DEFAULT_PARAMS.initialA);
  readonly b = signal(DEFAULT_PARAMS.initialB);

  readonly muX = computed(() => this.params().muX);
  readonly sigmaX = computed(() => this.params().sigmaX);
  readonly muY = computed(() => this.a() * this.muX() + this.b());
  readonly sigmaY = computed(() => Math.abs(this.a()) * this.sigmaX());
  readonly varX = computed(() => this.sigmaX() ** 2);
  readonly varY = computed(() => this.a() ** 2 * this.varX());

  readonly chartHeight = computed(() => this.params().chartHeight ?? 320);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly chartOptions = computed<any>(() => buildChartOptions(this.muX(), this.sigmaX(), this.a(), this.b()));

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));

  readonly formulaEHtml = computed<SafeHtml>(() => {
    const a = fmt(this.a());
    const b = fmt(this.b());
    const muX = fmt(this.muX());
    const muY = fmt(this.muY());
    const latex = `\\operatorname{E}({${ORANGE}{${a}}}X + {${INDIGO}{${b}}}) = {${ORANGE}{${a}}} \\cdot ${muX} + {${INDIGO}{${b}}} = ${muY}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly formulaVarHtml = computed<SafeHtml>(() => {
    const a = fmt(this.a());
    const a2 = fmt(this.a() ** 2);
    const b = fmt(this.b());
    const varX = fmt(this.varX());
    const varY = fmt(this.varY());
    const latex = `\\operatorname{Var}({${ORANGE}{${a}}}X + {${INDIGO}{${b}}}) = {${ORANGE}{${a}}}^2 \\cdot ${varX} = {${ORANGE}{${a2}}} \\cdot ${varX} = ${varY}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly formulaSigmaHtml = computed<SafeHtml>(() => {
    const sigmaY = fmt(this.sigmaY());
    const latex = `\\sigma = ${sigmaY}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly onAChange = (value: number): void => {
    this.a.set(value);
  };

  readonly onBChange = (value: number): void => {
    this.b.set(value);
  };
}

function fmt(n: number): string {
  return parseFloat(n.toFixed(2)).toString();
}
